import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

export function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.properties.stats(),
  });

  const { data: recentProperties, isLoading: propertiesLoading } = useQuery({
    queryKey: ['properties', 'recent'],
    queryFn: () => api.properties.list({ limit: '6' }),
  });

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.health(),
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Market Intelligence for Logistics Real Estate in DACH & Poland
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Properties</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {statsLoading ? '...' : stats?.totals?.total_properties?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">Cities Covered</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {statsLoading ? '...' : stats?.totals?.total_cities?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">Data Sources</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {statsLoading ? '...' : stats?.totals?.total_brokers?.toLocaleString() || '0'}
          </p>
        </div>
      </div>

      {/* Market Overview by Country */}
      {stats?.by_country_type && stats.by_country_type.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Market Overview</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Count
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Avg Rent
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Avg Size
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.by_country_type.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.country}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {row.property_type || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.count}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {row.avg_rent ? `${row.avg_rent.toFixed(2)} EUR/m²` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {row.avg_size ? `${Math.round(row.avg_size).toLocaleString()} m²` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Properties */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Properties</h2>
          <Link to="/properties" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View all
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {propertiesLoading ? (
            <p className="text-gray-500">Loading...</p>
          ) : recentProperties?.data?.length ? (
            recentProperties.data.map((property) => (
              <Link key={property.id} to={`/properties/${property.id}`} className="card p-4 hover:shadow-md transition-shadow">
                <h3 className="font-medium text-gray-900 truncate">{property.title}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {property.city}, {property.country}
                </p>
                <div className="mt-3 flex justify-between text-sm">
                  <span className="text-gray-600">
                    {property.total_area_sqm?.toLocaleString()} m²
                  </span>
                  {property.rent_eur_sqm && (
                    <span className="font-medium text-primary-600">
                      {property.rent_eur_sqm.toFixed(2)} EUR/m²
                    </span>
                  )}
                </div>
              </Link>
            ))
          ) : (
            <p className="text-gray-500">No properties found</p>
          )}
        </div>
      </div>

      {/* System Status */}
      {health && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">System Status</span>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                health.status === 'healthy'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {health.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
